import type { TableColumn } from "@general-backoffice/core";
import { Table, TableCard } from "@general-backoffice/core/index";
import moment from "moment";
import React from "react";
import { useTranslation } from "react-i18next";
import { Card } from "reactstrap";

import { LegalFooter } from "../legal";
import useTradesByBot from "../trades/hooks/useTradesByBot";

const positionTypes: Record<string, React.ReactNode> = {
  POSITION_TYPE_SELL: <small className="text-red font-weight-600">SHORT</small>,
  POSITION_TYPE_BUY: <small className="text-green font-weight-600">LONG</small>,
};

interface TradesHistoryCardProps {
  bot?: { id?: number | string } | null;
  isLoading?: boolean;
}

const TradesHistoryCard = ({ bot, isLoading }: TradesHistoryCardProps) => {
  const { t } = useTranslation("bots", { keyPrefix: "tradesHistoryTableCard" });

  const {
    trades,
    isLoading: isLoadingTrades,
    pagination,
    goToPage,
    changePageSize,
  } = useTradesByBot(bot?.id, {
    pagination: { pageSize: 5 },
    sort: { timestamp: "down" },
    autoLoad: !!bot?.id,
  });

  const columns: TableColumn[] = [
    {
      name: t("headers.date"),
      formattedContent: (row) => {
        const mm = moment(row.timestamp);
        return (
          <div className="my--3">
            <div className="font-weight-500">{mm.format("HH:mm:ss.SSS").replaceAll(":", " : ")}</div>
            <small>{mm.format("L")}</small>
          </div>
        );
      },
    },
    {
      name: t("headers.position"),
      formattedContent: (row) => positionTypes?.[row.type],
    },
    {
      name: t("headers.symbol"),
      property: "symbol",
    },
    {
      name: t("headers.amount"),
      property: "amount",
    },
    {
      name: t("headers.price"),
      property: "price",
    },
  ];

  return (
    <Card>
      <TableCard.Header title={t("title")} />
      <Table
        columns={columns}
        rows={trades}
        isLoading={isLoading || isLoadingTrades}
        noContentMessage={t("empty")}
        rowHeight={53}
        rowAmount={pagination?.pageSize}
        selectable={false}
        sortable={false}
      />
      <TableCard.Footer
        amount={pagination?.pageSize}
        setAmount={changePageSize}
        totalEntries={pagination?.total}
        page={pagination?.page - 1}
        onChangePage={(page) => goToPage(page + 1)}
        isLoading={isLoading}
      />
      <div className="px-3 pb-2">
        <LegalFooter type="broker" />
      </div>
    </Card>
  );
};

export default TradesHistoryCard;
